<template>
  <div
    ref="container"
    role="group"
    class="slide relative"
    :class="{ vertical }"
    :aria-label="containerLabel"
  >
    <label :for="`${elId}-input`" class="sr-only">{{ inputLabel }}</label>
    <div ref="input" class="input-wrapper before:bg-gray-100 rounded">
      <input
        :id="`${elId}-input`"
        :value="index"
        type="range"
        aria-live="polite"
        class="h-1 outline-none slider-thumb element-focusable"
        :class="{ debug: debugRangeSnapPoints && DEV }"
        min="0"
        step="1"
        :max="rangeMax"
        @input="onInput"
        @change="onCommit"
        @mousemove="handleMouseMove"
        @mouseleave="hoveredPosition = null"
        @blur="hoveredPosition = null"
        @keydown="onKeyDown"
        @keyup="onKeyUp"
      />
    </div>
    <div
      ref="tooltip"
      class="tooltip slide__tooltip absolute left-auto text-center"
      :class="{ ['slide__tooltip-show']: hoveredPosition !== null }"
      :style="toolTipStyle"
    >
      <span class="line-clamp">
        {{ labelAtPosition(visualIndex) }}
      </span>
    </div>
    <template v-if="debugRangeSnapPoints && DEV">
      <div
        v-for="pos of snapPoints"
        :key="pos"
        class="snap"
        :style="{ [vertical ? 'top' : 'left']: `${pos}px` }"
      ></div>
    </template>
  </div>
</template>
<script setup lang="ts">
import { computed, ref, useId, useTemplateRef, watchEffect } from "vue";

import {
  useCssVar,
  useDebounceFn,
  useThrottleFn,
  useElementBounding,
} from "@vueuse/core";

import { DEV } from "@/consts";
import { clamp, round } from "../../../modules/shared-utils/math";

// set to true to debug the snap points visually
const debugRangeSnapPoints = false;

const index = defineModel<number>("index", { type: Number, default: 0 });

const props = defineProps<{
  containerLabel: string;
  inputLabel: string;
  labelAtPosition: (index: number) => string;
  rangeMax: number;
  vertical: boolean;
}>();

const input = useTemplateRef<HTMLDivElement>("input");
const { height: inputHeight, width: inputWidth } = useElementBounding(input);
const inputLength = computed(() =>
  props.vertical ? inputHeight.value : inputWidth.value
);

const tooltip = useTemplateRef<HTMLDivElement>("tooltip");
const { width: tooltipWidth } = useElementBounding(tooltip);

const container = useTemplateRef<HTMLDivElement>("container");
const { height: containerHeight } = useElementBounding(container);

const verticalWidth = useCssVar("--vertical-width", input);
watchEffect(() => (verticalWidth.value = `${containerHeight.value}px`));

const progressLength = useCssVar("--progress-length", input, {
  initialValue: "0px",
});

const hoveredPosition = ref<number>(null);

// snap points are the positions in pixels where the thumb will snap to
// they are "squeezed" from each side based on the thumb size
// see: https://stackoverflow.com/questions/48880523/how-to-precisely-find-thumb-position-of-input-type-range
const snapPoints = computed(() => {
  // the thumb size is 1rem (tailwind h-4 w-4)
  const thumbWidth =
    parseFloat(window.getComputedStyle(document.documentElement).fontSize) ||
    16;

  return new Array(props.rangeMax + 1).fill(null).map((_, index) => {
    const ratio = index / (props.rangeMax || 1);
    return ratio * (inputLength.value - thumbWidth) + thumbWidth / 2;
  });
});

function closestIndex(position: number): number {
  let closest = 0;
  let distance = Math.abs(snapPoints.value[0] - position);
  const length = snapPoints.value.length;
  for (let index = 0; index < length; index++) {
    const point = snapPoints.value[index];
    const currentDistance = Math.abs(point - position);
    if (currentDistance <= distance) {
      distance = currentDistance;
      closest = index;
    } else {
      // when the distance increases, we can stop
      break;
    }
  }
  return closest;
}

type InputUpdateEvent = Event | { value: string };

function onInput(event: InputUpdateEvent) {
  const target = "target" in event ? (event.target as HTMLInputElement) : event;
  const index = parseInt(target.value) || 0;
  const activePosition = snapPoints.value[index];
  if (hoveredPosition.value === null) {
    return (hoveredPosition.value = activePosition);
  }

  const hoveredIndex = closestIndex(hoveredPosition.value);
  if (index === hoveredIndex) {
    // we're moving the slider with the mouse
    // don't use the index position to prevent flickering
    return;
  }

  // we're probably moving the slider with the keyboard
  hoveredPosition.value = activePosition;
}

function onCommit(event: InputUpdateEvent) {
  const target = "target" in event ? (event.target as HTMLInputElement) : event;
  index.value = parseInt(target.value) || 0;
}

const debouncedCommit = useDebounceFn(onCommit, 800, { maxWait: 3000 });

const keys = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"];
function onKeyUp(event: KeyboardEvent) {
  // prevent listeners elsewhere from firing for those keys
  if (!keys.includes(event.key)) return;
  event.stopPropagation();
  event.preventDefault();
}

async function onKeyDown(event: KeyboardEvent) {
  if (!keys.includes(event.key)) {
    return;
  }
  event.stopPropagation();
  event.preventDefault();

  let targetIndex = index.value;
  if (hoveredPosition.value !== null) {
    targetIndex = closestIndex(hoveredPosition.value);
  }

  if (shouldDecrease(event)) {
    targetIndex--;
  } else if (shouldIncrease(event)) {
    targetIndex++;
  } else {
    return;
  }

  if (targetIndex < 0 || targetIndex > props.rangeMax) return;
  hoveredPosition.value = snapPoints.value[targetIndex];

  const value = { value: targetIndex.toString() };
  onInput(value);

  await debouncedCommit(value);
}

function shouldDecrease(event: KeyboardEvent): boolean {
  if (event.key === "ArrowLeft") return true;
  if (event.key === "ArrowUp") return props.vertical;
  if (event.key === "ArrowDown") return !props.vertical;
  return false;
}

function shouldIncrease(event: KeyboardEvent): boolean {
  if (event.key === "ArrowRight") return true;
  if (event.key === "ArrowUp") return !props.vertical;
  if (event.key === "ArrowDown") return props.vertical;
  return false;
}

const visualIndex = computed<number>(() => {
  if (hoveredPosition.value !== null) {
    return closestIndex(hoveredPosition.value);
  }
  return parseInt((index.value as unknown as string) || "0");
});

watchEffect(() => {
  let value = 0;
  if (hoveredPosition.value !== null) {
    // directly in pixels
    value = clamp(hoveredPosition.value, 0, inputLength.value);
  } else {
    // from index to pixels
    const max = props.rangeMax || 1;
    value = round((index.value / max) * inputLength.value, 3);
  }
  progressLength.value = `${value}px`;
});

const toolTipStyle = computed(() => {
  let top: string = "auto";
  let left: string = "auto";
  let pointerLeft = "0";

  const toolTipOverflow =
    (parseFloat(window.getComputedStyle(document.documentElement).fontSize) ||
      16) * 1.5; // 1.5rem

  let progressPosition = snapPoints.value[index.value];
  if (hoveredPosition.value !== null) {
    progressPosition = hoveredPosition.value;
  }

  if (props.vertical) {
    top = `${progressPosition}px`;
    left = "-20px";
  }
  // if tooltip will leave slider area from left side
  else if (progressPosition < tooltipWidth.value / 2 - toolTipOverflow) {
    left = "-1.5rem";
    pointerLeft = `calc(${progressPosition}px + 0.5rem)`;
  }
  // if tooltip will leave slider area from right side
  else if (
    progressPosition >
    inputLength.value - tooltipWidth.value / 2 + toolTipOverflow
  ) {
    const slideLeft = inputLength.value - tooltipWidth.value;
    left = `calc(${slideLeft}px + 1.5rem)`;
    pointerLeft = `calc(${tooltipWidth.value - (inputLength.value - progressPosition)}px - 2.5rem)`;
  }
  // if tooltip is inside slider area
  else {
    left = `${progressPosition - tooltipWidth.value / 2}px`;
    pointerLeft = `calc(${tooltipWidth.value / 2}px - 1rem)`;
  }

  return { left, top, ["--pointer-left"]: pointerLeft };
});

const elId = useId();

const handleMouseMove = useThrottleFn((event: MouseEvent) => {
  if (!input.value) return;
  hoveredPosition.value = clamp(
    event.offsetX,
    snapPoints.value[0],
    snapPoints.value[snapPoints.value.length - 1]
  );
}, 100);
</script>
<style lang="scss" scoped>
@use "@/assets/styles/variables.scss" as *;

.debug {
  // strangely they override each other,
  // so we need to set them conditionally and duplicate the rules
  @supports selector(::-webkit-slider-runnable-track) {
    &::-webkit-slider-runnable-track {
      background-color: rgba(0, 255, 0, 0.3);
      border-radius: 0.25rem;
    }
  }
  @supports selector(::-moz-range-track) {
    &::-moz-range-track {
      background-color: rgba(0, 255, 0, 0.3);
      border-radius: 0.25rem;
    }
  }
}

// class to visually debug the snap points
.snap {
  position: absolute;
  width: 1rem;
  height: 1rem;
  pointer-events: none;
  transform: translate(-50%, -50%);
  background: var(--DM__theme-slider-color);
  opacity: 0.5;
  .vertical & {
    right: -1.5rem;
  }
}

// the input needs a wrapper because Safari doesn't render ::before and ::after on <input />
.input-wrapper {
  position: relative;
  isolation: isolate;

  display: flex;
  align-items: center;

  height: 1rem;
  width: 100%;

  input {
    appearance: none;
    background: transparent;
    cursor: pointer;
  }

  input, // the input itself
  &::before, // the track indicator
  &::after // the progress indicator
  {
    height: 0.25rem;
    width: 100%;
  }

  // the progress indicator, over the track
  &::after {
    width: var(--progress-length);
    background: var(--DM__theme-slider-color);
  }

  &::before,
  &::after {
    content: "";
    position: absolute;
    box-sizing: border-box;
    left: 0;

    // ensure the track and progress are behind the thumb
    z-index: -1;

    border-radius: 0.25rem;
  }

  .vertical & {
    transform-origin: top left;
    transform: rotate(90deg) // main transform to have a working vertical slider
      // this adjustment is to re-align the slider with the parent relative positions
      translateY(-1rem);
    width: var(--vertical-width);
  }
}

.tooltip {
  display: flex;
  align-items: center;
  justify-content: center;
  bottom: $spacingXL;
  border-radius: $spacingS;
  background: var(--DM__theme-fill-color);
  color: var(--DM__theme-font-color);
  transform-style: preserve-3d;

  width: $tooltipSmallScreen;
  @media (min-width: $screenS) {
    width: $tooltipWidth;
  }

  opacity: 0;
  height: 0;
  padding: 0;
  border: 0;
  overflow: hidden;

  transition: opacity 0.1s ease-in;
  @mixin tooltip-visible {
    opacity: 1;
    height: $tooltipHeight;
    padding: $spacingS $spacingM;
    border: 3px solid var(--DM__theme-slider-color);
    overflow: inherit;
  }

  &.show {
    @include tooltip-visible;
  }

  @media (any-hover: hover) {
    display: none;

    .input-wrapper:hover + &,
    .input-wrapper:focus-within + & {
      display: flex;
      @include tooltip-visible;
    }
  }

  .vertical & {
    transform: translate(calc(-100% - 15px), -50%);
  }

  &::before {
    content: "";
    position: absolute;
    width: 0;
    height: 0;
    bottom: -2px;
    border-left: 1rem solid transparent;
    border-right: 1rem solid transparent;
    border-top: 1rem solid var(--DM__theme-slider-color);
    transform: translate(-3px, 100%);
    left: var(--pointer-left);

    .vertical & {
      bottom: auto;
      right: -2px;
      transform: translateX(100%);
      left: auto;
      border-right: 0;
      border-bottom: 1rem solid transparent;
      border-top: 1rem solid transparent;
      border-left: 1rem solid var(--DM__theme-slider-color);
    }
  }
}

.slide {
  padding: $spacingS 0;

  &.vertical {
    width: 1rem;
    height: 100%;
    margin: auto;
  }
}
</style>
